import { Cookie, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '@/components/Footer';

const Cookies = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <Link
            to="/"
            className="text-2xl font-bold text-capledger-teal hover:opacity-80 transition-opacity"
          >
            CapLedger
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="space-y-8">
          {/* Title Section */}
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-foreground">Cookie & Tracking Policy</h1>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div>
                <strong>Effective Date:</strong> October 2025
              </div>
              <div>
                <strong>Entity:</strong> Spesvest (Pty) Ltd — CapLedger
              </div>
            </div>
          </div>

          {/* Policy Content */}
          <div className="prose prose-slate max-w-none space-y-8">
            {/* Section 1 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">1. What Are Cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                Cookies are small text files placed on your device that help CapLedger operate
                efficiently, remember preferences, and analyse traffic patterns.
              </p>
            </section>

            {/* Section 2 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">2. Types of Cookies We Use</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border border-border px-4 py-3 text-left font-semibold">
                        Category
                      </th>
                      <th className="border border-border px-4 py-3 text-left font-semibold">
                        Purpose
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr>
                      <td className="border border-border px-4 py-3 font-semibold">
                        Essential Cookies
                      </td>
                      <td className="border border-border px-4 py-3">
                        Required for login, security, and platform stability.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border px-4 py-3 font-semibold">
                        Analytics Cookies
                      </td>
                      <td className="border border-border px-4 py-3">
                        Help us understand usage (via internal analytics or trusted third-party
                        tools).
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border px-4 py-3 font-semibold">
                        Preference Cookies
                      </td>
                      <td className="border border-border px-4 py-3">
                        Store user settings like language or theme.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border px-4 py-3 font-semibold">
                        Marketing Cookies (optional)
                      </td>
                      <td className="border border-border px-4 py-3">
                        Used only with explicit consent for product updates or remarketing.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 3 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">3. Managing Cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                You can control or delete cookies via your browser settings. Essential cookies
                cannot be disabled because they are required for secure login.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                We will display a cookie banner on your first visit allowing you to accept all,
                reject non-essential, or customize preferences.
              </p>
            </section>

            {/* Section 4 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">4. Third-Party Services</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may use privacy-compliant analytics and logging services (such as BetterStack or
                PostHog). These providers operate under strict data-processing agreements and do not
                sell or share your data.
              </p>
            </section>

            {/* Section 5 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">5. Changes to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may revise this Cookie Policy as technologies evolve. Updates will appear at{' '}
                <a
                  href="https://www.capledger.co.za/cookies"
                  className="text-capledger-teal hover:underline"
                >
                  www.capledger.co.za/cookies
                </a>
                .
              </p>
            </section>

            {/* Section 6 - Contact */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">6. Contact</h2>
              <p className="text-muted-foreground leading-relaxed">
                For any cookie or privacy queries, contact:
              </p>
              <div className="bg-muted p-6 rounded-lg space-y-2">
                <a
                  href="mailto:privacy@capledger.co.za"
                  className="flex items-center gap-2 text-capledger-teal hover:underline"
                >
                  <Mail className="h-4 w-4" />
                  privacy@capledger.co.za
                </a>
                <p className="text-muted-foreground text-sm">
                  Spesvest (Pty) Ltd, Unit C, Doornbosch Centre, Strand Rd, Stellenbosch, South
                  Africa, 7600
                </p>
              </div>
            </section>
          </div>

          {/* Trust Badge */}
          <div className="flex items-center justify-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
            <Cookie className="h-6 w-6 text-capledger-teal" />
            <span className="text-sm text-muted-foreground">
              Your privacy choices are respected · We use cookies responsibly
            </span>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Cookies;
